import fetch from "cross-fetch";
import { RequestTypes } from "@enums/request-types";
import { QueryParameter } from "@idw-types/common";
import { FIELD_ALLOWED_FOR_NULL_VALUES, NULL_ENCODED_VALUE } from "@constants/verification-transaction-values";
import { apiRoutes } from "@constants/api-routes";
import { LoginErrorCodeTypes } from "@enums/login-error-code-types";
import { ResetSessionEventObject, UpdateSessionEventObject } from "@idw-types/update-session-event-object";
import { RESET_SESSION_TIMEOUT_EVENT_NAME } from "@constants/session-constants";

export const getHostnameWithScheme = () => {
    return `${window.location.protocol}//${window.location.host}`;
};

export const encodeParameterValue = (queryParameter: QueryParameter): string => {
    if (FIELD_ALLOWED_FOR_NULL_VALUES.includes(queryParameter.parameter) && queryParameter.value.includes(NULL_ENCODED_VALUE)) {
        const { value } = queryParameter;
        const encodedValue = encodeURIComponent(value.replace(NULL_ENCODED_VALUE, 'NOTAVAILABLE'));
        return encodedValue.replace('NOTAVAILABLE', NULL_ENCODED_VALUE)
    }

    return encodeURIComponent(queryParameter.value);
}

export const handleGetFileFromS3Request = async (
    path: string,
    signal?: AbortSignal
): Promise<{ data?: any, error?: any }> => {
    const url = `https://s3.amazonaws.com${path}`;
    return await handleRequestInternal(RequestTypes.GET, url, null, false, signal);
};

export const handleGetThemingCssFile = async (
    url: string,
    signal?: AbortSignal
): Promise<{ data?: any, error?: any }> => {
    return await handleRequestInternal(RequestTypes.GET, url, null, false, signal);
};

const getBaseUrl = (route: string) => {
    return `${getHostnameWithScheme()}${route}?_cbiddw=${new Date().getTime()}`;
};

const getUrlWithQueryParams = (route: string, queryParams: Array<QueryParameter>) => {
    let url = getBaseUrl(route);
    if (queryParams?.length) {
        url += "&";
        queryParams.forEach((queryParameter: QueryParameter, index: number) => {
            url += `${queryParameter.parameter}=${encodeParameterValue(queryParameter)}`;
            if (index !== queryParams.length - 1) {
                url += "&";
            }
        });
    }
    return url;
};

export const handleRequest = async (
    method: RequestTypes,
    route: string,
    signal?: AbortSignal,
): Promise<{
    data?: any;
    error?: any;
}> => {
    const url = getBaseUrl(route);
    return await handleRequestInternal(method, url, null, false, signal);
};

export const handleRequestWithQueryParams = async (
    method: RequestTypes,
    route: string,
    queryParams: Array<QueryParameter>,
    signal?: AbortSignal
): Promise<{ data?: any; error?: any; }> => {
    const url = getUrlWithQueryParams(route, queryParams);
    return await handleRequestInternal(method, url, null, false, signal);
};

export const handleRequestWithBody = async (
    method: RequestTypes,
    route: string,
    body: any,
    signal?: AbortSignal
): Promise<{ data?: any; error?: any; }> => {
    const url = getBaseUrl(route);
    return await handleRequestInternal(method, url, body, false, signal);
};

export const handleRequestWithQueryParamsAndBody = async (
    method: RequestTypes,
    route: string,
    queryParams: Array<QueryParameter>,
    body: any,
    signal?: AbortSignal
): Promise<{ data?: any; error?: any; }> => {
    const url = getUrlWithQueryParams(route, queryParams);
    return await handleRequestInternal(method, url, body, false, signal);
};

const handleRequestInternal = async (
    method: RequestTypes,
    url: string,
    body: any,
    isSessionRetry: boolean,
    signal?: AbortSignal
): Promise<{
    data?: any;
    error?: any;
}> => {

    let request: RequestInit;
    switch (method) {
        case RequestTypes.POST:
            request = {
                method: RequestTypes.POST,
                credentials: "omit",
                headers: {
                    "Accept": "application/json, text/plain, */*",
                    "Content-Type": "application/json"
                },
                signal,
                body: body ? JSON.stringify(body) : undefined
            };
            break;
        case RequestTypes.PUT:
            request = {
                method: RequestTypes.PUT,
                credentials: "omit",
                headers: {
                    "Accept": "application/json, text/plain, */*",
                    "Content-Type": "application/json"
                },
                signal,
                body: body ? JSON.stringify(body) : undefined
            }
            break;
        default:
            request = {
                method: RequestTypes.GET,
                headers: {
                    "Accept": "application/json, text/plain, */*"
                },
                signal
            };
            break;
    }

    let caughtError;
    try {
        const response = await fetch(url, request);
        if (response) {
            if (response.status < 400) {
                const resetSessionEvent = new CustomEvent<ResetSessionEventObject>(
                    RESET_SESSION_TIMEOUT_EVENT_NAME, {
                    detail: { shouldResetTimeout: true }
                });
                window.dispatchEvent(resetSessionEvent);

                if (response.status === 204) {
                    return {};
                } else {
                    const jsonResponse = await response.json();
                    return { data: jsonResponse };
                }
            } else {
                //unauthorized, refresh the users session
                if (response.status === 403 && !isSessionRetry) {
                    const newSession = await handleRequestInternal(
                        RequestTypes.GET,
                        apiRoutes.axnadminCore.session,
                        null,
                        true
                    );

                    if (newSession?.data) {
                        const updateSessionEvent = new CustomEvent<UpdateSessionEventObject>(
                            "updateSessionEvent", {
                            detail: { session: newSession.data }
                        });
                        window.dispatchEvent(updateSessionEvent);
                    }
                }


                if (!url.includes("session") && response.status === 401) {
                    const updateSessionEvent = new CustomEvent<UpdateSessionEventObject>(
                        "updateSessionEvent", {
                        detail: { session: null, errorCode: LoginErrorCodeTypes.USER_NOT_FOUND }
                    });

                    window.dispatchEvent(updateSessionEvent);
                }

                const jsonResponse = await response.json();
                return { error: JSON.stringify(jsonResponse) };
            }
        }
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('AbortError: Fetch request aborted');
        } else {
            caughtError = error;
        }
    }

    return { error: caughtError };
};